<template>
  <div class="chat-input bg-white border-t border-gray-200 p-3">
    <div v-if="isUploading" class="upload-progress mb-2 px-2">
      <div class="text-xs text-gray-600 mb-1">Uploading file...</div>
      <div class="h-1 w-full bg-gray-200 rounded">
        <div class="h-1 bg-primary rounded" :style="{ width: `${uploadProgress}%` }"></div>
      </div>
    </div>
    
    <div class="flex items-center gap-2">
      <!-- File upload button -->
      <label class="flex-shrink-0 p-2 text-gray-500 hover:text-primary cursor-pointer" :class="{ 'opacity-50 cursor-not-allowed': disabled }">
        <input type="file" class="hidden" @change="handleFileUpload" :disabled="disabled" />
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
      </label>
      
      <!-- Text input -->
      <div class="flex-grow relative">
        <textarea 
          ref="textarea"
          v-model="message" 
          @input="autoResize"
          @keydown.enter="handleKeyDown"
          :placeholder="placeholder"
          :disabled="disabled"
          class="w-full py-2 px-3 pr-10 border rounded-lg focus:outline-none focus:border-primary resize-none bg-gray-50"
          :class="{ 'opacity-70': disabled }"
          rows="1"
          :style="{ maxHeight: '150px', minHeight: '40px', height: textareaHeight }"
        ></textarea>
      </div>
      
      <!-- Send button -->
      <button 
        @click="sendMessage" 
        class="flex-shrink-0 p-2 rounded-full text-white bg-primary disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
        :disabled="!canSend || disabled"
        aria-label="Send message"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { socketService } from '../services/socket.service';
import { useMessageStore, type Message } from '../store/messages';

const props = defineProps<{
  placeholder?: string;
  disabled?: boolean;
  conversationId: string;
}>();

const emit = defineEmits<{
  (event: 'send', content: string): void;
  (event: 'upload', file: File): void;
  (event: 'typing', isTyping: boolean): void;
}>();

const messageStore = useMessageStore();

// Input state
const message = ref('');
const textarea = ref<HTMLTextAreaElement | null>(null);
const textareaHeight = ref('40px');

// Upload state
const isUploading = ref(false);
const uploadProgress = ref(0);

// Computed properties
const canSend = computed(() => message.value.trim().length > 0);

const defaultPlaceholder = computed(() => 
  props.placeholder || 'Type a message...'
);

// Methods
const sendMessage = async () => {
  if (!canSend.value || props.disabled) return;
  
  const content = message.value;
  message.value = '';
  textareaHeight.value = '40px';
  
  // No typing indicator logic needed for user input
  
  // Add message to store with temporary ID and sending status
  const tempMessage: Message = {
    id: `temp-${Date.now()}`,
    content,
    type: 'text',
    senderType: 'user',
    timestamp: Date.now(),
    status: 'sending',
  };
  
  messageStore.addMessage(tempMessage);
  
  try {
    // Send message through socket - this should get immediate acknowledgment from backend
    const messageId = await socketService.sendMessage(props.conversationId, content);
    
    // Update message with real ID and mark as sent (backend received and acknowledged)
    messageStore.updateMessage(tempMessage.id, {
      id: messageId as string,
      status: 'sent'
    });
  } catch (error) {
    console.error('[Chat] Failed to send message:', error);
    messageStore.updateMessageStatus(tempMessage.id, 'error');
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  // Send message on Enter (without shift key)
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    sendMessage();
  }
  // Allow new line with Shift+Enter (do nothing special, default behavior)
};

const autoResize = () => {
  if (!textarea.value) return;
  
  // Reset height to get the actual scrollHeight
  textarea.value.style.height = '40px';
  
  // Set to scrollHeight
  const scrollHeight = textarea.value.scrollHeight;
  textarea.value.style.height = Math.min(scrollHeight, 150) + 'px';
  textareaHeight.value = Math.min(scrollHeight, 150) + 'px';
};

const handleFileUpload = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0 || props.disabled) return;
  
  const file = input.files[0];
  
  // Simulate upload progress
  isUploading.value = true;
  uploadProgress.value = 0;
  
  const interval = setInterval(() => {
    uploadProgress.value += 10;
    if (uploadProgress.value >= 100) {
      clearInterval(interval);
      setTimeout(() => {
        isUploading.value = false;
        uploadProgress.value = 0;
        emit('upload', file);
        // Clear the input so the same file can be uploaded again
        if (input) input.value = '';
      }, 300);
    }
  }, 200);
};

// Lifecycle hooks
onMounted(() => {
  if (textarea.value) {
    textarea.value.addEventListener('input', autoResize);
  }
});

onUnmounted(() => {
  if (textarea.value) {
    textarea.value.removeEventListener('input', autoResize);
  }
});
</script>

<style scoped>
.chat-input {
  flex-shrink: 0;
}

textarea {
  line-height: 1.5;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

textarea::-webkit-scrollbar {
  width: 6px;
}

textarea::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

textarea::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}
</style> 